import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const WorkItemIdSchema = Type.String({
  pattern: "^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$",
});
const PreviewIdSchema = Type.String({
  pattern: "^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$",
});
const IdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128 });
const MoneyFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const PositiveMoneyFenSchema = Type.Integer({ minimum: 1, maximum: 100_000_000 });
const PositiveWalletAmountSchema = Type.Integer({ minimum: 1, maximum: 1_000_000_000_000 });
const PaymentEvidenceReferenceSchema = Type.String({
  minLength: 6,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:/-]{5,127}$",
});
const ReconciliationNoteSchema = Type.String({
  minLength: 2,
  maxLength: 500,
  pattern: "^\\S(?:[\\s\\S]{0,498}\\S)$",
});

export const MemberRechargeReconciliationOutcomeSchema = Type.Union([
  Type.Literal("CONFIRMED"),
  Type.Literal("NOT_RECEIVED"),
  Type.Literal("AMOUNT_MISMATCH"),
  Type.Literal("DUPLICATE_PAYMENT"),
]);
export type MemberRechargeReconciliationOutcome = Static<
  typeof MemberRechargeReconciliationOutcomeSchema
>;

export const MemberRechargeReconciliationPreviewRequestSchema = Type.Object(
  {
    outcome: MemberRechargeReconciliationOutcomeSchema,
    actualReceivedFen: Type.Union([PositiveMoneyFenSchema, Type.Null()]),
    paymentEvidenceReference: Type.Union([PaymentEvidenceReferenceSchema, Type.Null()]),
    note: ReconciliationNoteSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type MemberRechargeReconciliationPreviewRequest = Static<
  typeof MemberRechargeReconciliationPreviewRequestSchema
>;

export const CloudMemberRechargeReconciliationPreviewRequestSchema = Type.Object(
  {
    workItemId: WorkItemIdSchema,
    outcome: MemberRechargeReconciliationOutcomeSchema,
    actualReceivedFen: Type.Union([PositiveMoneyFenSchema, Type.Null()]),
    paymentEvidenceReference: Type.Union([PaymentEvidenceReferenceSchema, Type.Null()]),
    note: ReconciliationNoteSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CloudMemberRechargeReconciliationPreviewRequest = Static<
  typeof CloudMemberRechargeReconciliationPreviewRequestSchema
>;

export function isMemberRechargeReconciliationPreviewRequest(
  value: unknown,
): value is MemberRechargeReconciliationPreviewRequest {
  if (!Value.Check(MemberRechargeReconciliationPreviewRequestSchema, value)) return false;
  if (value.outcome === "NOT_RECEIVED") {
    return value.actualReceivedFen === null && value.paymentEvidenceReference === null;
  }
  if (value.outcome === "DUPLICATE_PAYMENT") {
    return value.actualReceivedFen === null && value.paymentEvidenceReference !== null;
  }
  return value.actualReceivedFen !== null && value.paymentEvidenceReference !== null;
}

export const MemberRechargeWalletCreditSchema = Type.Object(
  {
    walletName: Type.String({ minLength: 1, maxLength: 40 }),
    unit: Type.Union([
      Type.Literal("CNY_FEN"),
      Type.Literal("POINT"),
      Type.Literal("MINUTE"),
      Type.Literal("COUNT"),
      Type.Literal("CUSTOM"),
    ]),
    customUnitLabel: Type.Union([Type.String({ minLength: 1, maxLength: 12 }), Type.Null()]),
    precision: Type.Integer({ minimum: 0, maximum: 4 }),
    businessType: Type.Union([
      Type.Literal("STORED_VALUE"),
      Type.Literal("PROMOTIONAL"),
      Type.Literal("TIME_CREDIT"),
      Type.Literal("LOYALTY"),
      Type.Literal("COUNT_CREDIT"),
      Type.Literal("CUSTOM"),
    ]),
    amount: PositiveWalletAmountSchema,
  },
  { additionalProperties: false },
);
export type MemberRechargeWalletCredit = Static<typeof MemberRechargeWalletCreditSchema>;

export const MemberRechargeReconciliationPreviewSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    previewId: PreviewIdSchema,
    workItemId: WorkItemIdSchema,
    outcome: MemberRechargeReconciliationOutcomeSchema,
    memberName: Type.String({ minLength: 1, maxLength: 80 }),
    method: Type.Union([Type.Literal("STATIC_WECHAT"), Type.Literal("STATIC_ALIPAY")]),
    requestedPaidAmountFen: PositiveMoneyFenSchema,
    actualReceivedFen: Type.Union([PositiveMoneyFenSchema, Type.Null()]),
    cnyWalletCreditTotalFen: MoneyFenSchema,
    walletCredits: Type.Array(MemberRechargeWalletCreditSchema, { maxItems: 20 }),
    balanceEffectOnConfirmation: Type.Union([Type.Literal("CREDITED"), Type.Literal("UNCHANGED")]),
    paymentEvidenceEnding: Type.Union([Type.String({ minLength: 2, maxLength: 8 }), Type.Null()]),
    note: Type.String({ minLength: 2, maxLength: 500 }),
    createdAt: Type.String({ format: "date-time" }),
    expiresAt: Type.String({ format: "date-time" }),
    confirmationToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type MemberRechargeReconciliationPreview = Static<
  typeof MemberRechargeReconciliationPreviewSchema
>;

export function isMemberRechargeReconciliationPreview(
  value: unknown,
): value is MemberRechargeReconciliationPreview {
  if (!Value.Check(MemberRechargeReconciliationPreviewSchema, value)) return false;
  const createdAt = Date.parse(value.createdAt);
  const expiresAt = Date.parse(value.expiresAt);
  const credited = value.balanceEffectOnConfirmation === "CREDITED";
  return (
    Number.isFinite(createdAt) &&
    Number.isFinite(expiresAt) &&
    expiresAt - createdAt === 5 * 60_000 &&
    value.walletCredits.every(
      (credit) => (credit.unit === "CUSTOM") === (credit.customUnitLabel !== null),
    ) &&
    value.cnyWalletCreditTotalFen ===
      value.walletCredits
        .filter((credit) => credit.unit === "CNY_FEN")
        .reduce((sum, credit) => sum + credit.amount, 0) &&
    credited === value.walletCredits.length > 0 &&
    (credited ? value.actualReceivedFen !== null : value.actualReceivedFen === null) &&
    (value.outcome === "CONFIRMED"
      ? value.actualReceivedFen === value.requestedPaidAmountFen
      : true) &&
    (value.outcome === "AMOUNT_MISMATCH"
      ? value.actualReceivedFen !== null &&
        value.actualReceivedFen !== value.requestedPaidAmountFen &&
        value.walletCredits.length === 1 &&
        value.walletCredits[0]?.businessType === "STORED_VALUE" &&
        value.walletCredits[0]?.unit === "CNY_FEN" &&
        value.cnyWalletCreditTotalFen === value.actualReceivedFen
      : true) &&
    (value.outcome === "NOT_RECEIVED" || value.outcome === "DUPLICATE_PAYMENT"
      ? !credited
      : credited) &&
    (value.outcome === "NOT_RECEIVED"
      ? value.paymentEvidenceEnding === null
      : value.paymentEvidenceEnding !== null)
  );
}

export const ConfirmMemberRechargeReconciliationRequestSchema = Type.Object(
  {
    previewId: PreviewIdSchema,
    confirmationToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ConfirmMemberRechargeReconciliationRequest = Static<
  typeof ConfirmMemberRechargeReconciliationRequestSchema
>;

export const MemberRechargeReconciliationResultSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    workItemId: WorkItemIdSchema,
    previewId: PreviewIdSchema,
    outcome: MemberRechargeReconciliationOutcomeSchema,
    workItemStatus: Type.Literal("RESOLVED"),
    rechargeStatus: Type.Union([
      Type.Literal("CONFIRMED_RECONCILED"),
      Type.Literal("RECONCILIATION_CLOSED"),
    ]),
    memberName: Type.String({ minLength: 1, maxLength: 80 }),
    method: Type.Union([Type.Literal("STATIC_WECHAT"), Type.Literal("STATIC_ALIPAY")]),
    requestedPaidAmountFen: PositiveMoneyFenSchema,
    actualReceivedFen: Type.Union([PositiveMoneyFenSchema, Type.Null()]),
    cnyWalletCreditTotalFen: MoneyFenSchema,
    walletCredits: Type.Array(MemberRechargeWalletCreditSchema, { maxItems: 20 }),
    paymentEvidenceEnding: Type.Union([Type.String({ minLength: 2, maxLength: 8 }), Type.Null()]),
    note: Type.String({ minLength: 2, maxLength: 500 }),
    reconciledAt: Type.String({ format: "date-time" }),
    balanceEffect: Type.Union([Type.Literal("CREDITED"), Type.Literal("UNCHANGED")]),
  },
  { additionalProperties: false },
);
export type MemberRechargeReconciliationResult = Static<
  typeof MemberRechargeReconciliationResultSchema
>;

export function isMemberRechargeReconciliationResult(
  value: unknown,
): value is MemberRechargeReconciliationResult {
  if (!Value.Check(MemberRechargeReconciliationResultSchema, value)) return false;
  const credited = value.balanceEffect === "CREDITED";
  return (
    credited === (value.rechargeStatus === "CONFIRMED_RECONCILED") &&
    credited === value.walletCredits.length > 0 &&
    value.walletCredits.every(
      (credit) => (credit.unit === "CUSTOM") === (credit.customUnitLabel !== null),
    ) &&
    value.cnyWalletCreditTotalFen ===
      value.walletCredits
        .filter((credit) => credit.unit === "CNY_FEN")
        .reduce((sum, credit) => sum + credit.amount, 0) &&
    (credited ? value.actualReceivedFen !== null : value.actualReceivedFen === null) &&
    (value.outcome === "CONFIRMED"
      ? value.actualReceivedFen === value.requestedPaidAmountFen
      : true) &&
    (value.outcome === "AMOUNT_MISMATCH"
      ? value.actualReceivedFen !== null &&
        value.actualReceivedFen !== value.requestedPaidAmountFen &&
        value.walletCredits.length === 1 &&
        value.walletCredits[0]?.businessType === "STORED_VALUE" &&
        value.walletCredits[0]?.unit === "CNY_FEN" &&
        value.cnyWalletCreditTotalFen === value.actualReceivedFen
      : true) &&
    (value.outcome === "NOT_RECEIVED" || value.outcome === "DUPLICATE_PAYMENT"
      ? !credited
      : credited) &&
    (value.outcome === "NOT_RECEIVED"
      ? value.paymentEvidenceEnding === null
      : value.paymentEvidenceEnding !== null)
  );
}
